import React, { useEffect, useState } from 'react';
import { Edit2, Plus, Save, Trash2, X } from 'lucide-react';
import api from '../lib/api';
import ImageUploadField from '../components/ImageUploadField';
import HelpCallout from './HelpCallout';
import TrashPanel from './TrashPanel';

const TIERS = ['gold', 'silver', 'bronze'];

function empty() {
  return { slug: '', name: '', tagline: '', description: '', hero_image: '', tier: 'silver', website: '', town: '', category: '', published: true };
}

export default function AdminMembers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  function load() { api.get('/admin/members').then((r) => setItems(r.data.items)); }
  useEffect(load, []);

  async function save() {
    const payload = { ...editing };
    const src = payload._source || 'members';
    try {
      if (src === 'attractions') {
        // Attractions-sourced members are stored in the `attractions`
        // collection. Map member-form fields onto the attraction schema
        // and PATCH there — the member_tier drives what shows up on the
        // Members admin.
        await api.patch(`/admin/attractions/${payload.slug}`, {
          name: payload.name,
          summary: payload.tagline,
          description: payload.description,
          hero_image: payload.hero_image,
          website: payload.website,
          town: payload.town,
          member_tier: payload.tier,
          published: payload.published,
        });
      } else if (creating) {
        if (!payload.slug) payload.slug = payload.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        delete payload._source;
        await api.post('/admin/members', payload);
      } else {
        delete payload._source;
        await api.patch(`/admin/members/${editing.slug}`, payload);
      }
      setEditing(null); setCreating(false); load();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
  }
  async function del(slug, source) {
    if (source === 'attractions') {
      if (!window.confirm('This will remove the member badge (bronze/silver/gold) from the attraction — the attraction itself stays in the directory. Continue?')) return;
      try {
        await api.patch(`/admin/attractions/${slug}`, { member_tier: '' });
        load();
      } catch (e) { alert(e.response?.data?.detail || 'Update failed'); }
      return;
    }
    if (!window.confirm('Delete member?')) return;
    await api.delete(`/admin/members/${slug}`); load();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label text-heritage mb-2">Featured members</div>
          <h1 className="font-display text-4xl">Paying members ({items.length})</h1>
        </div>
        <button onClick={() => { setEditing(empty()); setCreating(true); }} data-testid="admin-member-new"
          className="bg-forest text-base px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-forest-deep">
          <Plus size={14}/> Add member
        </button>
      </div>

      <HelpCallout
        title="Your paying members get visibility everywhere — this is their reward."
        description={`Each card here gets a special "Featured Member" badge across the site. Gold tier surfaces first; Silver and Bronze still benefit but with slightly less prominence. The Friday newsletter rotates one member spotlight each week — fairly, oldest-first.`}
        appearsAt={[
          { label: 'Homepage members marquee', where: 'auto-scrolling strip near the bottom (sorted gold → silver → bronze)' },
          { label: 'Homepage "Standout days out" tetris grid', where: 'features attractions tagged as members' },
          { label: 'Attraction card badge', where: 'gold "Featured Member" chip on /explore grid + detail' },
          { label: 'Friday newsletter ✨ Member spotlight section', where: 'rotates round-robin across all published members' },
        ]}
      />

      <TrashPanel
        resourcePath="/admin/members"
        resourceLabel="Member"
        idField="slug"
        nameField="name"
        onChange={load}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((m) => (
          <div key={m.slug} className="bg-white border border-charcoal/10 rounded-2xl overflow-hidden" data-testid={`member-card-admin-${m.slug}`}>
            <div className="aspect-[16/9] overflow-hidden">{m.hero_image ? <img src={m.hero_image} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full bg-stone flex items-center justify-center text-charcoal/40 text-sm">No image</div>}</div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-1">
                <div className={`label ${m.tier === 'gold' ? 'text-mustard' : 'text-charcoal/60'}`}>{m.tier} member</div>
                <span className="text-[10px] uppercase tracking-wider text-charcoal/40" data-testid={`member-source-${m.slug}`} title={m._source === 'attractions' ? 'Managed via the attraction record' : 'Managed via the Members collection'}>
                  via {m._source || 'members'}
                </span>
              </div>
              <h3 className="font-display text-xl mb-1">{m.name}</h3>
              <p className="text-muted text-sm line-clamp-2 mb-4">{m.tagline}</p>
              <div className="flex gap-2">
                <button onClick={() => { setEditing({ ...m }); setCreating(false); }} className="flex-1 bg-admin border border-charcoal/15 px-3 py-2 rounded-full text-xs font-semibold inline-flex items-center justify-center gap-1" data-testid={`member-edit-${m.slug}`}><Edit2 size={12}/> Edit</button>
                <button onClick={() => del(m.slug, m._source)} className="bg-admin border border-heritage/30 text-heritage px-3 py-2 rounded-full text-xs font-semibold" data-testid={`member-delete-${m.slug}`}><Trash2 size={12}/></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-charcoal/10 p-6 flex items-center justify-between">
              <h3 className="font-display text-2xl">{creating ? 'New member' : editing.name}</h3>
              <button onClick={() => { setEditing(null); setCreating(false); }} className="p-2 hover:bg-stone rounded-lg"><X size={18}/></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <label className="block"><div className="label mb-1.5">Name</div><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
                <label className="block"><div className="label mb-1.5">Tier</div>
                  <select value={editing.tier} onChange={(e) => setEditing({ ...editing, tier: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5" data-testid="member-tier">
                    {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              </div>
              <label className="block"><div className="label mb-1.5">Tagline</div><input value={editing.tagline} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
              <label className="block"><div className="label mb-1.5">Description</div><textarea rows={4} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
              <ImageUploadField
                value={editing.hero_image}
                onChange={(v) => setEditing({ ...editing, hero_image: v })}
                label="Hero image"
                testidPrefix="member-hero"
              />
              <div className="grid grid-cols-2 gap-4">
                <label className="block"><div className="label mb-1.5">Website</div><input value={editing.website || ''} onChange={(e) => setEditing({ ...editing, website: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
                <label className="block"><div className="label mb-1.5">Town</div><input value={editing.town || ''} onChange={(e) => setEditing({ ...editing, town: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
              </div>
              <label className="flex items-center gap-3 bg-admin border border-charcoal/10 rounded-xl px-4 py-3">
                <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })}/>
                <span className="text-sm font-semibold">Published</span>
              </label>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-charcoal/10 p-6 flex justify-end gap-3">
              <button onClick={() => { setEditing(null); setCreating(false); }} className="px-5 py-2.5 rounded-full font-semibold border border-charcoal/15">Cancel</button>
              <button onClick={save} data-testid="member-save" className="bg-forest text-base px-6 py-2.5 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-forest-deep"><Save size={14}/> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
